import { __assign, __awaiter, __generator } from "tslib";
import { bootstrapFeature, preloadFeature, } from '@ms/centro-hvc-loader';
import { getCentroUrl, initializeLogging, mergeObjects, PerfPrefix } from '@ms/ocv-inapp-feedback-shared';
import { getConfigFromInitOptions, LogError as logErrorInternal, LogInfo as logInfoInternal, promiseWithTimeout, } from '@ms/ocv-inapp-feedback-shared';
import { CentroDataSourcesString } from './Constants';
import { getCentroInitParameters, setInitTelemetryData, validateInitializationParameters } from './SupportFunctions';
var InAppFeedbackInitOptionsListener = (function () {
    var feedbackData = {};
    // register listener to Centro to enable dynamic data passing
    var register = function (listener) {
        if (listener) {
            listeners.push(listener);
        }
        return feedbackData;
    };
    // unregister listener from Centro
    var unregister = function (listener) {
        var index = listeners.indexOf(listener);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    };
    // list of function provided to update Feedback configuration
    var listeners = [];
    /**
     *  Updates existing properties of the current instance
     */
    var updateFeedbackObject = function (updatedInAppFeedbackInitOptions) {
        var updatedFeedbackData = mergeObjects({}, feedbackData, updatedInAppFeedbackInitOptions);
        updatedFeedbackData = setInitTelemetryData(updatedFeedbackData);
        listeners.forEach(function (listener) { return listener(updatedFeedbackData); });
    };
    /**
     * Opens new feedback discarding old values
     */
    var openFeedbackObject = function (initFeedbackData) {
        initFeedbackData.feedbackConfig.isDisplayed = true;
        initFeedbackData = setInitTelemetryData(initFeedbackData);
        listeners.forEach(function (listener) { return listener(initFeedbackData); });
    };
    var configObject = {
        register: register,
        unregister: unregister,
        updateFeedbackObject: updateFeedbackObject,
        openFeedbackObject: openFeedbackObject,
    };
    return configObject;
})();
/**
 * Asynchronously launches the feedback mechanism within the application.
 *
 * This function initializes the feedback system with the provided initialization information
 * and in-app feedback options. It is designed to be called when the application
 * needs to trigger the feedback UI, allowing users to submit their feedback based on the
 * current state and configuration of the application.
 *
 * @param initializationInfo - Contains essential data required to initialize the feedback CDN.
 * @param inAppFeedbackInitOptions - Provides options to customize the in-app feedback experience.
 */
var launchFeedback = function (initializationInfo, inAppFeedbackInitOptions) { return __awaiter(void 0, void 0, void 0, function () {
    var getConfigFromInitOptionsOperation, bootstrapper, _a, e_1, _b, e_2;
    var _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    return __generator(this, function (_p) {
        switch (_p.label) {
            case 0:
                inAppFeedbackInitOptions.performanceTimestamps = __assign(__assign({}, inAppFeedbackInitOptions.performanceTimestamps), { integrationInitTimestamp: Date.now(), useCentroConfig: initializationInfo.useCentroConfig, isPrefetched: (_c = window.__inappfeedback_centro_integration_config) === null || _c === void 0 ? void 0 : _c.isPrefetched });
                initializationInfo.cloudType || (initializationInfo.cloudType = (_e = (_d = inAppFeedbackInitOptions.feedbackConfig) === null || _d === void 0 ? void 0 : _d.complianceChecks) === null || _e === void 0 ? void 0 : _e.tenantCloudType);
                initializationInfo.tenantId || (initializationInfo.tenantId = inAppFeedbackInitOptions.tenantId);
                return [4 /*yield*/, validateInitializationParameters(initializationInfo)];
            case 1:
                _p.sent();
                setupGlobalIntegrationConfig();
                getConfigFromInitOptionsOperation = getConfigFromInitOptions(inAppFeedbackInitOptions, 'ocv-inapp-feedback');
                if ((_f = window.__inappfeedback_centro_integration_config) === null || _f === void 0 ? void 0 : _f.unmountFunction) {
                    window.__inappfeedback_centro_integration_config.unmountFunction();
                }
                return [4 /*yield*/, window.__inappfeedback_centro_integration_config.loadInAppFeedbackPackage(initializationInfo, inAppFeedbackInitOptions === null || inAppFeedbackInitOptions === void 0 ? void 0 : inAppFeedbackInitOptions.clientFeedbackId)];
            case 2:
                _p.sent();
                _p.label = 3;
            case 3:
                _p.trys.push([3, 6, , 7]);
                logInfoInternal("532392" /* LoggingTags.FeedbackInitiated */, 'DOM Bootstrap Started.', {
                    clientFeedbackId: (_g = inAppFeedbackInitOptions === null || inAppFeedbackInitOptions === void 0 ? void 0 : inAppFeedbackInitOptions.clientFeedbackId) !== null && _g !== void 0 ? _g : null,
                    isPrefetched: (_h = window.__inappfeedback_centro_integration_config) === null || _h === void 0 ? void 0 : _h.isPrefetched,
                });
                return [4 /*yield*/, window.__inappfeedback_centro_integration_config.bootstrapFeature(__assign({}, getCentroInitParameters(initializationInfo)))
                    // Mounting the HVC on the provided HTML element
                ];
            case 4:
                bootstrapper = _p.sent();
                // Mounting the HVC on the provided HTML element
                _a = window.__inappfeedback_centro_integration_config;
                return [4 /*yield*/, bootstrapper.main(initializationInfo.inAppFeedbackRef)];
            case 5:
                // Mounting the HVC on the provided HTML element
                _a.unmountFunction = _p.sent();
                logInfoInternal("532392" /* LoggingTags.FeedbackInitiated */, 'DOM Bootstrap Completed.', {});
                return [3 /*break*/, 7];
            case 6:
                e_1 = _p.sent();
                logErrorInternal("532385" /* LoggingTags.CentroBootstrapFailed */, "Centro DOM bootstrap failed", e_1, {
                    hostName: initializationInfo.hostName,
                    locale: initializationInfo.locale,
                    inAppFeedbackRefAvailable: initializationInfo.inAppFeedbackRef ? true : false,
                    specificHvcVersion: (_j = initializationInfo.specificHvcVersion) !== null && _j !== void 0 ? _j : null,
                    majorVersion: (_k = initializationInfo.majorVersion) !== null && _k !== void 0 ? _k : null,
                    centroUrl: (_l = initializationInfo.centroUrl) !== null && _l !== void 0 ? _l : null,
                });
                return [3 /*break*/, 7];
            case 7:
                logInfoInternal("532386" /* LoggingTags.FeedbackComponentInitializationCompleted */, 'InAppFeedback Component Initialization Completed.', {});
                _p.label = 8;
            case 8:
                _p.trys.push([8, 10, , 11]);
                _b = inAppFeedbackInitOptions;
                return [4 /*yield*/, promiseWithTimeout('fetchCloudCofig', getConfigFromInitOptionsOperation, 1000)];
            case 9:
                _b.cloudConfig = _p.sent();
                return [3 /*break*/, 11];
            case 10:
                e_2 = _p.sent();
                logErrorInternal("543800" /* LoggingTags.CloudConfigTimeout */, "Fetching cloud config timed out", e_2, {
                    clientFeedbackId: (_m = inAppFeedbackInitOptions === null || inAppFeedbackInitOptions === void 0 ? void 0 : inAppFeedbackInitOptions.clientFeedbackId) !== null && _m !== void 0 ? _m : null,
                });
                return [3 /*break*/, 11];
            case 11:
                (_o = window.__inappfeedback_centro_integration_config) === null || _o === void 0 ? void 0 : _o.openFeedback(inAppFeedbackInitOptions);
                return [2 /*return*/];
        }
    });
}); };
function loadInAppFeedbackPackage(initializationInfo, clientFeedbackId) {
    return __awaiter(this, void 0, void 0, function () {
        var dataSources, centroAdapter, hostName, majorVersion, locale, useCentroConfig, inAppFeedbackRef, specificHvcVersion, centroUrl, preloadParameter;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    dataSources = function (sd) {
                        sd(CentroDataSourcesString, InAppFeedbackInitOptionsListener);
                    };
                    centroAdapter = {
                        dataSources: dataSources,
                    };
                    hostName = initializationInfo.hostName, majorVersion = initializationInfo.majorVersion, locale = initializationInfo.locale, useCentroConfig = initializationInfo.useCentroConfig, inAppFeedbackRef = initializationInfo.inAppFeedbackRef, specificHvcVersion = initializationInfo.specificHvcVersion, centroUrl = initializationInfo.centroUrl;
                    preloadParameter = __assign(__assign({}, getCentroInitParameters(initializationInfo)), { adapters: centroAdapter, appConfig: {
                            appId: window.__inappfeedback_centro_appid,
                            perfPrefix: "".concat(PerfPrefix, "_").concat(hostName),
                            useCentroConfig: useCentroConfig,
                        } });
                    return [4 /*yield*/, preloadFeature(preloadParameter).catch(function (e) {
                            initializeLogging({
                                centroEnvironmentBaseUrl: getCentroUrl(initializationInfo.cloudType),
                                hostName: initializationInfo.hostName,
                            });
                            logErrorInternal("532384" /* LoggingTags.CentroPreloadFeatureFailed */, "centro preloadFeature failed", e, {
                                hostName: hostName,
                                locale: locale,
                                inAppFeedbackRefAvailable: inAppFeedbackRef ? true : false,
                                specificHvcVersion: specificHvcVersion !== null && specificHvcVersion !== void 0 ? specificHvcVersion : null,
                                majorVersion: majorVersion !== null && majorVersion !== void 0 ? majorVersion : null,
                                centroUrl: centroUrl !== null && centroUrl !== void 0 ? centroUrl : null,
                            });
                        })];
                case 1:
                    _c.sent();
                    logInfoInternal("532444" /* LoggingTags.CentroRuntimeInitializeCompleted */, 'Centro Runtime Initialization and preloadFeature Completed.', {
                        clientFeedbackId: clientFeedbackId !== null && clientFeedbackId !== void 0 ? clientFeedbackId : null,
                        isPrefetched: (_b = (_a = window.__inappfeedback_centro_integration_config) === null || _a === void 0 ? void 0 : _a.isPrefetched) !== null && _b !== void 0 ? _b : false,
                    });
                    return [2 /*return*/];
            }
        });
    });
}
var setupGlobalIntegrationConfig = function () {
    if (!window.__inappfeedback_centro_integration_config) {
        window.__inappfeedback_centro_integration_config = {
            updateFeedbackOptions: InAppFeedbackInitOptionsListener.updateFeedbackObject,
            openFeedback: InAppFeedbackInitOptionsListener.openFeedbackObject,
            logInfo: logInfoInternal,
            logError: logErrorInternal,
            unmountFunction: function () { return undefined; },
            bootstrapFeature: bootstrapFeature,
            loadInAppFeedbackPackage: loadInAppFeedbackPackage,
            isPrefetched: false,
        };
    }
};
/**
 * Asynchronously preloads the in-app feedback package.
 *
 * This function is responsible for preloading necessary resources for the in-app feedback feature
 * based on the provided initialization information. It ensures that when the feedback feature is
 * triggered, it can be presented to the user with minimal delay, enhancing the user experience.
 *
 * Note: You would need to call launchFeedback to trigger the UI
 *
 * @param initializationInfo -  Contains essential data required to initialize the feedback CDN.
 */
function preloadInAppFeedbackPackage(initializationInfo) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setupGlobalIntegrationConfig();
                    window.__inappfeedback_centro_integration_config.isPrefetched = true;
                    return [4 /*yield*/, window.__inappfeedback_centro_integration_config.loadInAppFeedbackPackage(initializationInfo)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Use this function after you have invoked launchFeedback at least once.
 * This allows you to update the feedback options dynamically after the initial feedback launch.
 *
 * @param {IFeedbackInitOptions} updatedInAppFeedbackInitOptions - The updated feedback initialization options.
 */
var updateFeedbackOptions = function (updatedInAppFeedbackInitOptions) {
    var _a;
    if ((_a = window.__inappfeedback_centro_integration_config) === null || _a === void 0 ? void 0 : _a.updateFeedbackOptions) {
        window.__inappfeedback_centro_integration_config.updateFeedbackOptions(updatedInAppFeedbackInitOptions);
        return;
    }
    logErrorInternal("532388" /* LoggingTags.UpdateFeedbackOptionsUnavailable */, "updateFeedbackOptions not added to config", new Error(JSON.stringify(window.__inappfeedback_centro_integration_config)), {});
};
var logInfo = function (id, text, data) {
    var _a;
    if ((_a = window.__inappfeedback_centro_integration_config) === null || _a === void 0 ? void 0 : _a.logInfo) {
        window.__inappfeedback_centro_integration_config.logInfo(id, text, data);
        return;
    }
    logErrorInternal("532380" /* LoggingTags.LogInfoUnavailable */, "logInfo not added to config", new Error(JSON.stringify(window.__inappfeedback_centro_integration_config)), {});
};
var logError = function (id, text, error, data) {
    var _a;
    if ((_a = window.__inappfeedback_centro_integration_config) === null || _a === void 0 ? void 0 : _a.logError) {
        window.__inappfeedback_centro_integration_config.logError(id, text, error, data);
        return;
    }
    logErrorInternal("532381" /* LoggingTags.LogErrorUnavailable */, "logError not added to config", new Error(JSON.stringify(window.__inappfeedback_centro_integration_config)), {});
};
var unmountFeedback = function () {
    var _a;
    if ((_a = window.__inappfeedback_centro_integration_config) === null || _a === void 0 ? void 0 : _a.unmountFunction) {
        window.__inappfeedback_centro_integration_config.unmountFunction();
        return;
    }
    logErrorInternal("532387" /* LoggingTags.UnmountFeedbackUnavailable */, "unmountFeedback not added to config", new Error(JSON.stringify(window.__inappfeedback_centro_integration_config)), {});
};
export { logError, logInfo, preloadInAppFeedbackPackage, unmountFeedback, updateFeedbackOptions, launchFeedback };
